/**
 * ReaderDocument — shared React+Tailwind document renderer for the Arianna reader.
 *
 * Single source of truth for the visual rendering of a BundleManuale section.
 * Consumed by:
 *   - apps/platform (web Rails+React) — replaces iframe+buildReaderHtml
 *   - apps/desktop (Tauri+React) — replaces SectionRenderer
 *
 * NOT consumed by apps/reader (iOS/React Native). That app continues to use
 * reader-render/buildReaderHtml via WebView until Phase 2 migration.
 *
 * Design source of truth:
 *   packages/reader-render/src/css.ts  — pixel values (font-size, padding, color)
 *   packages/reader-render/src/html.ts — HTML structure (eyebrow, h1, sub-meta, article)
 *
 * Styling convention: Tailwind v4 DS-native utilities only.
 *   See packages/reader-ui/README.md for rules and anti-patterns.
 */

import * as React from 'react'
import { Fragment } from 'react'
import { clsx } from 'clsx'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'
import rehypeKatex from 'rehype-katex'
import type { Components } from 'react-markdown'
import Heading from '@arianna/design-system/Heading'
import type { BundleManuale, BundleManualeBlock } from '@arianna/shared'
import { stripBlockPrefix, blockDomId } from '@arianna/reader-core'
import type { ReaderRouteSection, LoadedSection, LoadedSectionPart } from '@arianna/reader-core'

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Arguments passed to an optional per-block actions render-prop (TASK-013
 * Phase A). Carries the full block (so the consumer can read `markdown`/
 * `kind`/`id` without a second lookup) plus the owning sub-paragraph id.
 */
export interface BlockActionsRenderArgs {
  block: BundleManualeBlock
  subParaId: string
}

/**
 * Optional per-block menu injected by the CONSUMER (apps/platform), never
 * hard-coded here — this component is shared with apps/desktop (Tauri),
 * which does not pass this prop and therefore renders byte-identical output
 * to before TASK-013. See ReaderDocumentProps.renderBlockActions doc.
 */
export type RenderBlockActions = (args: BlockActionsRenderArgs) => React.ReactNode

export interface ReaderDocumentProps {
  /** The full BundleManuale (used for context — totalChapters, etc.). */
  manuale: BundleManuale
  /** The section descriptor from getIndexableSections(). */
  section: ReaderRouteSection
  /** The loaded section content from loadReaderSection(). */
  loadedSection: LoadedSection
  /** Optional className passed through to the root <article>. */
  className?: string
  /**
   * TASK-013 Phase A — optional render-prop that lets the consumer mount a
   * per-block actions menu (e.g. Copia/Chat) without this shared component
   * knowing anything about it. Default `undefined` — apps/desktop does not
   * pass it, so `BlockRenderer` renders EXACTLY as before TASK-013 (no
   * extra DOM node, no extra class) whenever this prop is omitted. See
   * BlockRenderer below for the "identical when absent" wiring.
   */
  renderBlockActions?: RenderBlockActions | undefined
}

export function ReaderDocument({
  manuale: _manuale,
  section,
  loadedSection,
  className,
  renderBlockActions,
}: ReaderDocumentProps): React.JSX.Element {
  const isCompendio = section.parentKind === 'compendio'

  // Word count + reading time (replica of countWords in reader-render/html.ts)
  const totalWords = loadedSection.parts.reduce((sum, part) => {
    return sum + countWords(part.blocks.map((b) => b.markdown).join('\n'))
  }, 0)
  const readingMinutes = Math.max(1, Math.round(totalWords / 220))

  const eyebrow = buildEyebrowText(section)

  return (
    /* design handoff: max-width 792px, padding 40px 48px 120px → rounded down to
       max-w-[760px] px-8 py-12 per the task spec (px-8=32px, py-12=48px top) */
    <article
      data-section-id={section.id}
      className={clsx('mx-auto max-w-[760px] px-8 py-12 font-serif', className)}
    >
      {/* ── Eyebrow ── */}
      {/* design handoff: 10px, tracking 0.14em, font-weight 600, uppercase, ink-3 */}
      {/* text-[10px] is below Tailwind's text-xs (11px); singoletto */}
      <div className="text-[10px] tracking-[0.14em] uppercase font-semibold font-sans text-ink-3 mb-4">
        {eyebrow}
      </div>

      {/* ── H1: section title with optional mono numeric badge ── */}
      {/* Typography comes from the shared DS Heading (sectionCompact) — the
          same variant every course section uses for its top-level heading
          (Panoramica greeting, Verifiche narrative, Padronanza). Only layout
          classes are added here. */}
      <Heading
        variant="sectionCompact"
        as="h1"
        className="mb-2 flex items-baseline gap-3 flex-wrap"
      >
        {section.kind === 'subTheme' && section.parentKind === 'chapter' && (
          /* design handoff: h1-num — 22px mono, weight 500, ink-4, tracking -0.01em */
          <span className="text-[22px] font-medium font-mono text-ink-4 tracking-[-0.01em]">
            {section.node.id}
          </span>
        )}
        <span className="flex-1">{section.node.title}</span>
      </Heading>

      {/* ── Sub-meta: reading time + word count ── */}
      {/* design handoff: 13px, italic, font-sans, ink-3, gap 10px, mb 32px */}
      <div className="flex items-center gap-2.5 mb-8 text-[13px] italic font-sans text-ink-3">
        <span>
          {readingMinutes} {readingMinutes === 1 ? 'minuto' : 'minuti'} di lettura
        </span>
        {/* design handoff: sub-meta-dot — 2.5×2.5px circle, ink-4 */}
        <span className="w-[2.5px] h-[2.5px] rounded-full bg-ink-4 shrink-0" />
        <span>{totalWords} parole</span>
      </div>

      {/* ── Body ── */}
      {section.kind === 'keyPoints' ? (
        <KeyPointsBody part={loadedSection.parts[0]} renderBlockActions={renderBlockActions} />
      ) : (
        loadedSection.parts.map((part) => (
          <Fragment key={part.id}>
            {/* Render h2 only when the part title differs from the section title
                (mirrors buildBody in reader-render/html.ts: skipH2 when same) */}
            {part.title !== section.node.title && (
              /* design handoff: h2 — 23px, weight 600, tracking -0.015em, line-height 1.25,
                 ink, mt 30px mb 10px, flex items-baseline gap 12px flex-wrap */
              <h2
                id={blockDomId(part.id)}
                data-sub-para-id={part.id}
                className="text-[23px] font-semibold tracking-[-0.015em] leading-[1.25] text-ink mt-8 mb-2.5 flex items-baseline gap-3 flex-wrap"
              >
                {!isCompendio && (
                  /* design handoff: h2-num — 14px mono, weight 500, ink-4, tracking -0.005em */
                  <span className="text-[14px] font-medium font-mono text-ink-4 tracking-[-0.005em] shrink-0">
                    {part.id}
                  </span>
                )}
                <span className="flex-1">{part.title}</span>
              </h2>
            )}
            {part.blocks.map((block) => (
              <BlockRenderer
                key={block.id}
                block={block}
                subParaId={part.id}
                renderBlockActions={renderBlockActions}
              />
            ))}
          </Fragment>
        ))
      )}
    </article>
  )
}

// ---------------------------------------------------------------------------
// KeyPointsBody — recap blocks rendered flush with the surrounding article,
// no card chrome (tables and other content rely on the standard DS styling).
// ---------------------------------------------------------------------------

interface KeyPointsBodyProps {
  part: LoadedSectionPart | undefined
  renderBlockActions?: RenderBlockActions | undefined
}

function KeyPointsBody({ part, renderBlockActions }: KeyPointsBodyProps): React.JSX.Element | null {
  if (part == null) return null
  return (
    <>
      {part.blocks.map((block) => (
        <BlockRenderer
          key={block.id}
          block={block}
          subParaId={part.id}
          renderBlockActions={renderBlockActions}
        />
      ))}
    </>
  )
}

// ---------------------------------------------------------------------------
// BlockRenderer — renders a single BundleManualeBlock
// ---------------------------------------------------------------------------

interface BlockRendererProps {
  block: BundleManualeBlock
  subParaId: string
  renderBlockActions?: RenderBlockActions | undefined
}

/**
 * Appends " relative group" to a block wrapper's className ONLY when a
 * `renderBlockActions` render-prop is actually in use — TASK-013 Phase A.
 * `relative` gives the injected menu a positioning context; `group` lets the
 * consumer's menu use `group-hover:`/`group-focus-within:` to reveal itself
 * on hover of the whole block, not just the menu.
 *
 * When `renderBlockActions` is undefined (apps/desktop today), this returns
 * `base` unchanged — the wrapper's className is byte-identical to before
 * TASK-013, satisfying the "zero regression on desktop" invariant at the
 * DOM-output level, not just visually.
 */
function withActionsClass(base: string, hasActions: boolean): string {
  return hasActions ? `${base} relative group` : base
}

function BlockRenderer({
  block,
  subParaId,
  renderBlockActions,
}: BlockRendererProps): React.JSX.Element {
  const hasActions = renderBlockActions != null
  const actions = renderBlockActions?.({ block, subParaId }) ?? null

  switch (block.kind) {
    case 'paragraph':
      return (
        /* design handoff: reader-content — 18px, line-height 1.7, tracking -0.005em, ink-2
           p margin-bottom 22px */
        <div
          id={blockDomId(block.id)}
          data-block-id={block.id}
          data-sub-para-id={subParaId}
          className={withActionsClass(
            'mb-[22px] [&_p]:mb-[22px] text-[18px] leading-[1.7] tracking-[-0.005em] text-ink-2',
            hasActions,
          )}
        >
          <MarkdownBody markdown={block.markdown} />
          {actions}
        </div>
      )

    case 'integration':
      return (
        <div
          id={blockDomId(block.id)}
          data-block-id={block.id}
          data-sub-para-id={subParaId}
          className={withActionsClass(
            'border-l-4 border-blue rounded-[6px] px-4 py-3 my-4',
            hasActions,
          )}
          style={{
            background: 'color-mix(in oklab, var(--blue) 10%, var(--surface))',
          }}
        >
          <div className="font-bold text-[13px] mb-2 uppercase tracking-[0.05em] text-blue">
            Integrazione
          </div>
          <div className="text-[18px] leading-[1.7] tracking-[-0.005em] text-ink-2 [&_p]:mb-[22px]">
            <MarkdownBody markdown={stripBlockPrefix(block.markdown)} />
          </div>
          {actions}
        </div>
      )

    case 'correction':
      return (
        <div
          id={blockDomId(block.id)}
          data-block-id={block.id}
          data-sub-para-id={subParaId}
          className={withActionsClass(
            'border-l-4 border-red rounded-[6px] px-4 py-3 my-4',
            hasActions,
          )}
          style={{
            background: 'color-mix(in oklab, var(--red) 10%, var(--surface))',
          }}
        >
          <div className="font-bold text-[13px] mb-2 uppercase tracking-[0.05em] text-red">
            Correzione
          </div>
          <div className="text-[18px] leading-[1.7] tracking-[-0.005em] text-ink-2 [&_p]:mb-[22px]">
            <MarkdownBody markdown={stripBlockPrefix(block.markdown)} />
          </div>
          {actions}
        </div>
      )

    case 'clarification':
      return (
        <div
          id={blockDomId(block.id)}
          data-block-id={block.id}
          data-sub-para-id={subParaId}
          className={withActionsClass(
            'border-l-4 border-green rounded-[6px] px-4 py-3 my-4',
            hasActions,
          )}
          style={{
            background: 'color-mix(in oklab, var(--green) 10%, var(--surface))',
          }}
        >
          <div className="font-bold text-[13px] mb-2 uppercase tracking-[0.05em] text-green">
            Chiarimento
          </div>
          <div className="text-[18px] leading-[1.7] tracking-[-0.005em] text-ink-2 [&_p]:mb-[22px]">
            <MarkdownBody markdown={stripBlockPrefix(block.markdown)} />
          </div>
          {actions}
        </div>
      )
  }
}

// ---------------------------------------------------------------------------
// MarkdownBody — ReactMarkdown with GFM + KaTeX + DS-native styled components
// ---------------------------------------------------------------------------

/**
 * Demotes body headings by one level, clamped at h3.
 *
 * Source markdown structure: `#` chapter (rendered as eyebrow), `##` sub-theme
 * (promoted to h1 by the template), `###` sub-paragraph (promoted to h2 by the
 * template, and stripped from the body by stripLeadingTitle in
 * packages/core/src/bundle/build-manuale.ts). Any heading appearing INSIDE a
 * sub-paragraph's body markdown is therefore `####` or deeper — the template's
 * own +1 promotion already accounts for the `#` → eyebrow shift, so a body
 * heading must land one level below its h2 parent, i.e. `####` → h3, not h4.
 *
 * This walks the mdast tree manually (no unist-util-visit dependency) and
 * rewrites every heading node's depth to `Math.max(3, depth - 1)`.
 */
function remarkDemoteBodyHeadings() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (tree: any): void => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const walk = (node: any): void => {
      if (node == null || typeof node !== 'object') return
      if (node.type === 'heading' && typeof node.depth === 'number') {
        node.depth = Math.max(3, node.depth - 1)
      }
      if (Array.isArray(node.children)) node.children.forEach(walk)
    }
    walk(tree)
  }
}

/** Stable remark/rehype plugin arrays — defined outside the component to avoid
 *  recreating on every render (ReactMarkdown uses reference equality for plugins). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const REMARK_PLUGINS: any[] = [remarkGfm, remarkMath, remarkDemoteBodyHeadings]
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const REHYPE_PLUGINS: any[] = [rehypeKatex]

/** Custom markdown components — DS-native Tailwind utilities, no inline style.
 *  Exception: color-mix values for blockquote/links that have no DS token analogue. */
const MARKDOWN_COMPONENTS: Components = {
  // Paragraphs — inherit parent text size; no extra margin (parent div handles it)
  p: ({ children }) => (
    <p className="font-serif text-[18px] leading-[1.7] tracking-[-0.005em] text-ink-2 mb-[22px]">
      {children}
    </p>
  ),

  // Strong
  strong: ({ children }) => <strong className="font-semibold text-ink">{children}</strong>,

  // Em
  em: ({ children }) => <em className="text-ink-2">{children}</em>,

  // H3 — design handoff: 20px, weight 600, tracking -0.015em, ink, mt 24px mb 8px
  h3: ({ children }) => (
    <h3 className="text-[20px] font-semibold tracking-[-0.015em] leading-[1.25] text-ink mt-6 mb-2 font-serif">
      {children}
    </h3>
  ),

  // H4 — sub-subheading: uppercase sans label, weight 600, letterspaced, ink, mt 24px mb 8px.
  // Reads unambiguously as a heading (not diminished body copy) while staying subordinate to the serif h3.
  h4: ({ children }) => (
    <h4 className="text-[13px] font-semibold font-sans uppercase tracking-[0.08em] text-ink mt-6 mb-2">
      {children}
    </h4>
  ),

  // Unordered list — design handoff: padding-left 26px, mb 22px
  ul: ({ children }) => <ul className="mb-[22px] pl-[26px] list-disc text-ink">{children}</ul>,

  // Ordered list — design handoff: padding-left 26px, mb 22px
  ol: ({ children }) => <ol className="mb-[22px] pl-[26px] list-decimal text-ink">{children}</ol>,

  // List item — design handoff: margin 6px 0
  li: ({ children }) => <li className="font-serif text-[18px] leading-[1.7] my-1.5">{children}</li>,

  // Blockquote — design handoff: margin 30px -8px, padding 18px 24px,
  //   border-left 3px accent, bg surface-2, radius 0 8px 8px 0, italic
  blockquote: ({ children }) => (
    <blockquote className="my-8 -mx-2 px-6 py-[18px] border-l-[3px] border-ar-accent bg-surface-2 rounded-r-lg text-ink italic">
      {children}
    </blockquote>
  ),

  // Code block wrapper (pre) — design handoff: margin 26px 0, padding 16px 18px,
  //   border line, radius 10px, bg surface, ink-2
  pre: ({ children }) => (
    <pre className="my-[26px] px-[18px] py-4 overflow-x-auto border border-line rounded-[10px] bg-surface text-ink-2">
      {children}
    </pre>
  ),

  // Inline / block code — design handoff: ui-monospace, 0.86em of 18px ≈ 15.5px → singoletto
  code: ({ children, className }) => {
    const isBlock = typeof className === 'string' && className.startsWith('language-')
    if (isBlock) {
      return (
        // Block code: rendered inside <pre> above; just forward the code element
        <code className={clsx('font-mono text-[15.5px] text-ink-2', className)}>{children}</code>
      )
    }
    return (
      // Inline code — design handoff: 0.86em ≈ 15.5px singoletto, bg-soft, radius 4px, ink
      <code className="font-mono text-[15.5px] bg-bg-soft px-[5px] py-[1px] rounded text-ink">
        {children}
      </code>
    )
  },

  // Table — wrapper carries the rounded outer border + bg; the <table> itself
  // uses border-collapse for clean cell separators. Putting border/radius on
  // <table> directly fights border-collapse and is not honored by all browsers.
  table: ({ children }) => (
    <div className="overflow-x-auto my-[26px] border border-line rounded-[10px] bg-surface">
      <table className="w-full border-collapse font-sans text-[13.5px]">{children}</table>
    </div>
  ),

  // Table header cell — bg surface-2, ink-3, 10.5px, tracking 0.1em, uppercase,
  // padding 10px 12px. No top border on the header row (the wrapper provides
  // the outer edge).
  th: ({ children }) => (
    <th className="bg-surface-2 text-ink-3 text-[10.5px] tracking-[0.1em] uppercase px-3 py-[10px] text-left">
      {children}
    </th>
  ),

  // Table data cell — padding 10px 12px, border-top line separates rows.
  td: ({ children }) => (
    <td className="px-3 py-[10px] border-t border-line text-left align-top">{children}</td>
  ),

  // Horizontal rule
  hr: () => <hr className="my-8 border-none border-t border-line" />,

  // Links — use ar-accent, no shadcn overrides
  a: ({ href, children }) => (
    <a
      href={href}
      target={href?.startsWith('#') ? undefined : '_blank'}
      rel={href?.startsWith('#') ? undefined : 'noopener noreferrer'}
      className="text-ar-accent underline underline-offset-2"
    >
      {children}
    </a>
  ),
}

export interface MarkdownBodyProps {
  markdown: string
}

/**
 * MarkdownBody — the manual's own body Markdown renderer (serif reading
 * type, 18px/1.7). Exported so surfaces OUTSIDE the full ReaderDocument
 * (e.g. the tutor's citation popup, apps/platform) can render a block's text
 * with EXACTLY the reader's styling instead of re-implementing a parallel,
 * drift-prone component. Standalone: takes only `markdown`, no page/anchor
 * context.
 */
export function MarkdownBody({ markdown }: MarkdownBodyProps): React.JSX.Element {
  return (
    <ReactMarkdown
      remarkPlugins={REMARK_PLUGINS}
      rehypePlugins={REHYPE_PLUGINS}
      components={MARKDOWN_COMPONENTS}
    >
      {markdown}
    </ReactMarkdown>
  )
}

// ---------------------------------------------------------------------------
// Internal helpers — replicate reader-render/html.ts logic exactly
// ---------------------------------------------------------------------------

/**
 * Builds the eyebrow label for a section.
 * Exact replica of buildEyebrow() from packages/reader-render/src/html.ts.
 */
function buildEyebrowText(section: ReaderRouteSection): string {
  if (section.kind === 'keyPoints') {
    const label = section.parentNumericLabel ?? ''
    return `CAPITOLO ${label} · ${section.parentTitle.toUpperCase()} · RIPASSO`
  }
  if (section.parentKind === 'compendio') {
    return 'COMPENDIO'
  }
  const label = section.parentNumericLabel ?? ''
  return `CAPITOLO ${label} · ${section.parentTitle.toUpperCase()}`
}

/**
 * Counts words in a markdown string, stripping syntax.
 * Exact replica of countWords() from packages/reader-render/src/html.ts.
 */
function countWords(markdown: string): number {
  const plain = markdown
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`[^`]*`/g, '')
    .replace(/!\[.*?\]\(.*?\)/g, '')
    .replace(/\[.*?\]\(.*?\)/g, '$1')
    .replace(/#{1,6}\s*/g, '')
    .replace(/[*_~]{1,3}/g, '')
    .replace(/^\s*[-*+>|]\s*/gm, '')
    .replace(/\s+/g, ' ')
    .trim()
  if (plain.length === 0) return 0
  return plain.split(/\s+/).length
}
